import { LoadingButton } from "@mui/lab";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import {
  Typography,
  Button,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Link,
  Switch,
  Paper,
  Tooltip,
  Radio,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useState, useEffect, useMemo, useCallback } from "react";
import { updateCityConnects } from "../../../store/actions/userActions";
import { updateSubCityConnects } from "../../../store/actions/subUserActions";

const SetupJaxDetails = ({
  connectToCity,
  setConnectToCity,
  selectedCity,
  county,
  selectedCityUser,
  setSelectedCityUser,
  addNewUser,
  setAddNewUser,
  selectUserLoading,
  setSelectUserLoading,
  selectedForms,
  isNocSelected,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [cityLoading, setCityLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [warningText, setWarningText] = useState(
    "Your credentials are securely encrypted and remain inaccessible to anyone. We prioritize your privacy and use top-tier data security measures to protect your information.",
  );
  const [selectUserInfo, setSelectUserInfo] = useState(null); // { type: 'success' | 'error', message: string }
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null); // { id, user_name }

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isMediumUp = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const isLargeDown = useMediaQuery((theme) => theme.breakpoints.down("lg"));

  useEffect(() => {
    if (connectToCity && !isNocSelected) {
      setConnectToCity(false);
    }
  }, [connectToCity, isNocSelected, setConnectToCity]);

  const handleSelectUser = useCallback(
    async (row) => {
      if (selectedCityUser === row.id) {
        setSelectedCityUser("");
        setSelectUserInfo(null);
        setIsError(false);
        return;
      }

      // Validate credentials when selecting a user
      setSelectUserInfo(null);
      setIsError(false);
      setSelectUserLoading(row.id);

      try {
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/noc/validate-city-credentials`,
          {
            currentSession: currentSession,
            city_user_id: row.id,
            county: county,
            city: selectedCity,
          },
          { withCredentials: true },
        );

        if (resp?.data?.success) {
          setSelectedCityUser(row.id);
          setSelectUserInfo({
            type: "success",
            message: "Credentials validated successfully!",
          });
          setIsError(false);
        } else {
          setSelectedCityUser("");
          const errorMessage =
            resp?.data?.message ||
            "Invalid credentials! The password may have been changed.";
          setSelectUserInfo({
            type: "error",
            message: `${errorMessage} Please remove this user (using the delete icon) and add new credentials.`,
          });
          setIsError(true);
        }
        setSelectUserLoading(null);
      } catch (e) {
        setSelectedCityUser("");
        const errorMessage =
          e?.response?.data?.message ||
          "Invalid credentials! The password may have been changed.";
        setSelectUserInfo({
          type: "error",
          message: `${errorMessage} Please remove this user (using the delete icon) and add new credentials.`,
        });
        setIsError(true);
        setSelectUserLoading(null);
      }
    },
    [
      county,
      currentSession,
      selectedCity,
      selectedCityUser,
      setSelectUserLoading,
      setSelectedCityUser,
    ],
  );

  // Clear success message after 5 seconds, error messages after 10 seconds
  useEffect(() => {
    if (selectUserInfo) {
      const timeout = selectUserInfo.type === "success" ? 5000 : 10000;
      const timer = setTimeout(() => {
        setSelectUserInfo(null);
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [selectUserInfo]);

  const handleRemoveUserClick = useCallback(
    (row) => {
      // Find the user name from the row
      const user = currentUser?.city_connects?.[county.toLowerCase()]?.[
        selectedCity.toLowerCase()
      ]?.find((u) => u.id === row);

      setUserToDelete({ id: row, user_name: user?.user_name || "this user" });
      setDeleteModalOpen(true);
    },
    [county, currentUser?.city_connects, selectedCity],
  );

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const handleRemoveUser = async () => {
    if (!userToDelete) return;

    setWarningText("");
    setSelectUserInfo(null);
    setDeleteModalOpen(false);

    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/remove-city-credential`,
        {
          currentSession: currentSession,
          county: county,
          city: selectedCity,
          id: userToDelete.id,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setSelectedCityUser("");
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
      setUserToDelete(null);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again.",
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
      setUserToDelete(null);
    }
  };

  const handleAcceptCityScrape = async () => {
    setWarningText("");
    try {
      setCityLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/noc/save-city_credentials`,
        {
          currentSession: currentSession,
          username: username,
          password: password,
          county: county,
          city: selectedCity,
        },
        { withCredentials: true },
      );

      if (resp?.data?.success) {
        // await dispatch(updatePermitPackage(resp?.data?.data));
        if (currentSession === "userSession") {
          await dispatch(updateCityConnects(resp?.data?.data));
        } else {
          await dispatch(updateSubCityConnects(resp?.data?.data));
        }
        setUsername("");
        setPassword("");
        setAgreeToTerms(false);
        setUsernameError(false);
        setPasswordError(false);
        setIsError(false);
        setWarningText(
          "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
        );
      }
      setAddNewUser(false);
      setCityLoading(false);
    } catch (e) {
      setWarningText(
        "Login failed. Please check your credentials and try again.",
      );
      setIsError(true);
      setUsernameError(true);
      setPasswordError(true);
      setCityLoading(false);
    }
  };

  const cityRows = useMemo(
    () =>
      currentUser?.city_connects?.[county.toLowerCase()]?.[
        selectedCity.toLowerCase()
      ] ?? [],
    [currentUser, selectedCity, county],
  );

  const columns = useMemo(
    () => [
      {
        field: "user_name",
        headerName: "User",
        flex: 1,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Remove User">
              <IconButton
                color="error"
                size="small"
                onClick={() => handleRemoveUserClick(row.id)}
                aria-label="delete"
                disabled={cityLoading || selectUserLoading !== null}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2">{row.user_name}</Typography>
          </Box>
        ),
      },
      {
        field: "role",
        headerName: "City Portal Role",
        flex: 0.5,
        valueGetter: (value) =>
          value?.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) ??
          "",
      },
      {
        field: "date",
        headerName: "Added At",
        flex: 0.5,
        valueGetter: (value) =>
          value
            ? DateTime.fromISO(value, { zone: "utc" })
                .setZone("America/New_York")
                .toFormat("MM/dd/yyyy")
            : "",
      },
      {
        field: "select",
        headerName: "Select",
        flex: 0.3,
        sortable: false,
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) =>
          selectUserLoading === row.id ? (
            <CircularProgress size={20} />
          ) : (
            <Radio
              checked={selectedCityUser === row.id}
              onChange={() => handleSelectUser(row)}
              value={row.id}
              disabled={selectUserLoading !== null}
              size="small"
            />
          ),
      },
    ],
    [
      cityLoading,
      handleRemoveUserClick,
      handleSelectUser,
      selectUserLoading,
      selectedCityUser,
    ],
  );

  const mobileGridColumn = useMemo(
    () => [
      {
        field: "mobile",
        headerName: "Users",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};

          return (
            <Box
              sx={{
                width: "100%",
                py: 0.6,
                px: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.45,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {row?.user_name ?? ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      wordBreak: "break-word",
                    }}
                  >
                    {row?.role
                      ?.replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()) ?? ""}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: "rgba(0,0,0,0.62)",
                      flexShrink: 0,
                      lineHeight: 1.2,
                      mt: 0.25,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row?.date
                      ? DateTime.fromISO(row?.date, { zone: "utc" })
                          .setZone("America/New_York")
                          .toFormat("MM/dd/yyyy")
                      : ""}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Tooltip title="Remove User">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveUserClick(row?.id)}
                      aria-label="delete"
                      disabled={cityLoading || selectUserLoading !== null}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {selectUserLoading === row?.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Radio
                      checked={selectedCityUser === row?.id}
                      onChange={() => handleSelectUser(row)}
                      value={row?.id}
                      disabled={selectUserLoading !== null}
                      size="small"
                      sx={{ padding: "0px !important" }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        },
      },
    ],
    [
      cityLoading,
      handleRemoveUserClick,
      handleSelectUser,
      selectUserLoading,
      selectedCityUser,
    ],
  );

  return (
    <>
      <Box
        sx={{
          height: "calc(100vh - 230px)",
          overflowY: "auto",
        }}
      >
        <Typography
          variant="h5"
          mt={2}
          align="center"
          color="primary"
          gutterBottom
        >
          Automate Your Permit Process with the City of Jacksonville
        </Typography>

        <Typography
          variant="body2"
          fontWeight="bold"
          align="center"
          px={2}
          mb={2}
        >
          To save you time and reduce errors, Permit Rockstar offers full
          automation with the City of Jacksonville’s permit portal. If you
          enable this option, we’ll generate the permit number for you, pre-fill
          city forms using your selected data, and upload all required
          documents. This means you only need to review and submit. It’s fast,
          reliable, and completely secure.
        </Typography>

        <Box display="flex" justifyContent="center">
          <FormControlLabel
            control={
              <Switch
                //! Temporary disable the switch until the jaxepics scraper will be fixed
                // disabled={!isNocSelected}
                disabled={true}
                // checked={connectToCity}
                checked={false}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setUsername("");
                    setPassword("");
                    setAgreeToTerms(false);
                    setUsernameError(false);
                    setPasswordError(false);
                    setIsError(false);
                    setWarningText(
                      "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
                    );
                    setAddNewUser(false);
                    setSelectedCityUser("");
                    setSelectUserInfo(null);
                    setSelectUserLoading(null);
                  }
                  setConnectToCity(e.target.checked);
                }}
              />
            }
            label="Enable Automation for This Permit Package"
          />
        </Box>

        {/* Conditionally render based on the automation switch */}
        {connectToCity ? (
          // If user already gave consent before
          currentUser?.permit_consent?.[county.toLowerCase()]?.[
            selectedCity.toLowerCase()
          ]?.consent === true ? (
            <Box sx={{ mt: 4 }}>
              {/* You can render a mini table or a summary of saved city creds */}
              <Typography variant="h6" align="center" color="success.main">
                Your login credentials are already saved for this city.
              </Typography>
              <Typography
                align="center"
                fontWeight="bold"
                variant="body2"
                mt={0}
                mb={3}
              >
                If you’d like to use a different account, you can remove it
                below and enter new login details.
              </Typography>
              {/* Replace with actual component later */}
              <>[City Credentials Table / Remove Button / Replace Form]</>
            </Box>
          ) : (
            <Box mt={3}>
              {/* Login form when no consent yet */}
              <Typography variant="h6" align="center" color="primary">
                Enter Your City Website Login
              </Typography>
              <Typography align="center" variant="body2" mt={1} mb={2}>
                We'll use these credentials to log into the Jacksonville permit
                portal on your behalf and pre-fill everything for you.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Button
                  color={addNewUser ? "error" : "primary"}
                  variant="text"
                  sx={{ textDecoration: "underline", whiteSpace: "nowrap" }}
                  onClick={() => {
                    setUsername("");
                    setPassword("");
                    setAgreeToTerms(false);
                    setUsernameError(false);
                    setPasswordError(false);
                    setIsError(false);
                    setWarningText(
                      "Your credentials are securely encrypted and cannot be viewed or decrypted by anyone. We use the highest standards of data security to safeguard your information.",
                    );
                    setSelectedCityUser("");
                    setSelectUserInfo(null);

                    setAddNewUser(!addNewUser);
                  }}
                >
                  {!addNewUser ? "Add New User" : "Cancel"}
                </Button>
                {selectUserInfo && (
                  <Typography
                    variant="body2"
                    sx={{
                      pl: 2,
                      color:
                        selectUserInfo.type === "success"
                          ? "success.main"
                          : "error.main",
                      fontWeight: "bold",
                    }}
                  >
                    {selectUserInfo.message}
                  </Typography>
                )}
              </Box>
              {!addNewUser ? (
                <Paper sx={{ mt: 2, width: "100%" }}>
                  <DataGrid
                    rows={cityRows}
                    columns={
                      (isMediumUp && isLargeDown) || mobileSize
                        ? mobileGridColumn
                        : columns
                    }
                    initialState={{
                      pagination: { paginationModel: { pageSize: 5 } },
                    }}
                    pageSizeOptions={[5, 10, 25]}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    density="compact"
                    autoHeight
                    sx={{
                      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        "&:hover": {
                          ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                            {
                              visibility: "hidden",
                              pointerEvents: "none",
                            },
                          cursor: "default",
                        },
                        backgroundColor: "#333333", // Change column header background color
                        color: "white", // Optional: Change text color for contrast
                      },
                      // Additionally target specific sort button
                      "& .MuiDataGrid-columnHeader--sortable": {
                        pointerEvents: "none",
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        padding: 0, // Adjust as needed
                        margin: 0, // Adjust as needed
                      },
                      "& .MuiDataGrid-columnHeadersInner": {
                        width: "100%", // Adjust as needed
                        padding: 0,
                      },
                      border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
                      "& .MuiDataGrid-cell": {
                        borderColor: "#333333", // Change cell border color
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        display: "none", // Optional: Hide the column separators
                      },
                      "& .MuiDataGrid-row": {
                        cursor: "pointer", // Change cursor to pointer for rows
                        // backgroundColor: "#fff4c0 !important",
                      },
                      // Apply light yellow background for specified rows
                      "& .light-yellow-row": {
                        backgroundColor: "#fff4c0",
                      },
                      "& .selected-row-color": {
                        backgroundColor: "#ebf1f4 !important",
                      },
                      // Apply darker yellow only for selected rows that are light-yellow
                      "& .light-yellow-row.Mui-selected": {
                        backgroundColor: "#ffe499 !important", // Slightly darker yellow
                      },
                    }}
                    rowHeight={
                      (isMediumUp && isLargeDown) || mobileSize ? 94 : 50
                    }
                  />
                </Paper>
              ) : (
                <>
                  <Box component="form" sx={{ mt: 3, px: 0.5 }}>
                    <TextField
                      error={usernameError}
                      helperText={
                        usernameError ? "Please enter a valid username" : ""
                      }
                      disabled={cityLoading || selectUserLoading !== null}
                      label="City Website Username"
                      variant="filled"
                      fullWidth
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (usernameError) setUsernameError(false);
                        if (passwordError) setPasswordError(false);
                        if (
                          warningText !== "" &&
                          warningText ===
                            "Login failed. Please check your credentials and try again."
                        )
                          setWarningText("");
                        if (isError) setIsError(false);
                      }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      error={passwordError}
                      helperText={
                        passwordError ? "Please enter a valid password" : ""
                      }
                      disabled={cityLoading || selectUserLoading !== null}
                      label="City Website Password"
                      type="password"
                      variant="filled"
                      fullWidth
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError(false);
                        if (usernameError) setUsernameError(false);
                        if (
                          warningText !== "" &&
                          warningText ===
                            "Login failed. Please check your credentials and try again."
                        )
                          setWarningText("");
                        if (isError) setIsError(false);
                      }}
                      sx={{ mb: 2 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreeToTerms}
                          onChange={(e) => setAgreeToTerms(e.target.checked)}
                          name="agreeToTerms"
                        />
                      }
                      label={
                        <Typography variant="body2">
                          I agree to the{" "}
                          <Link
                            href="https://permitrockstar.com//terms-and-conditions/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Terms and Conditions
                          </Link>
                        </Typography>
                      }
                    />

                    <Typography
                      align="center"
                      color="error"
                      variant="body2"
                      sx={{ mt: 2, fontWeight: "bold" }}
                    >
                      {warningText}
                    </Typography>
                  </Box>

                  <Box mt={3} sx={{ display: "flex", flexDirection: "column" }}>
                    <LoadingButton
                      disabled={
                        !username ||
                        !password ||
                        !agreeToTerms ||
                        isError ||
                        selectUserLoading !== null
                      }
                      loading={cityLoading}
                      color="success"
                      onClick={handleAcceptCityScrape}
                      variant="contained"
                      fullWidth
                    >
                      Provide Consent
                    </LoadingButton>
                  </Box>
                </>
              )}
            </Box>
          )
        ) : (
          <Typography
            align="center"
            color={!isNocSelected ? "error" : "text.secondary"}
            mt={3}
          >
            {!isNocSelected
              ? "Notice of Commencement must be selected to enable automation for this permit package."
              : "You chose to skip automation for this permit package. You can proceed manually."}
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Remove User</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove user:{" "}
            <strong>{userToDelete?.user_name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. You will need to add the credentials
            again if you want to use this user.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={cityLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleRemoveUser}
            loading={cityLoading}
            variant="contained"
            color="error"
          >
            Remove
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SetupJaxDetails;
